import { useState } from 'react'
import { post } from '../lib/api'
import { entries } from '../lib/debuglog'
import { getLocale, t } from '../lib/i18n'
import Icon from './Icon'

/**
 * The bug door.
 *
 * Open to every signed-in account, including the delegates signing in with a
 * PIN on the tablet -- they are the people playing, so they are the people who
 * find the bugs. What they type becomes a ticket; what the app knows travels
 * with it.
 *
 * The context is attached WITHOUT asking. "What browser were you on, what did
 * the screen say, what were you doing" is the part a person cannot answer an
 * hour later and will not be asked for in the moment, and it is the part that
 * decides whether the bug is reproducible. Only errors are taken from the debug
 * log, never ordinary traffic: that buffer records method, path and status and
 * no bodies, and even so a full dump of it does not belong in a ticket anyone
 * might paste somewhere.
 */
export default function BugReport({ page }: { page: string }) {
  const [open, setOpen] = useState(false)
  const [title, setTitle] = useState('')
  const [detail, setDetail] = useState('')
  const [busy, setBusy] = useState(false)
  const [filed, setFiled] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!title.trim()) return
    setBusy(true)
    setError(null)
    try {
      const recent = entries()
        .filter((x) => x.level === 'error' || x.level === 'warn')
        .slice(-10)
        .map((x) => `${new Date(x.at).toISOString().slice(11, 19)} ${x.level} ${x.message}`)

      const r = await post<{ ticket: { id: string } }>('/api/bugs', {
        title: title.trim(),
        body: detail.trim(),
        context: {
          page,
          url: location.pathname + location.search,
          userAgent: navigator.userAgent,
          viewport: `${window.innerWidth}×${window.innerHeight}`,
          locale: getLocale(),
          recent,
        },
      })
      setFiled(r.ticket.id)
      setTitle('')
      setDetail('')
    } catch (err) {
      setError(err instanceof Error ? err.message : t('error.generic'))
    } finally {
      setBusy(false)
    }
  }

  if (!open) {
    return (
      <button className="bug-tab" onClick={() => { setOpen(true); setFiled(null) }} title={t('bug.report')}>
        <Icon name="bug" size={16} /> {t('bug.report')}
      </button>
    )
  }

  return (
    <div className="bug-panel card" role="dialog" aria-label={t('bug.report')}>
      <div className="row">
        <strong>{t('bug.report')}</strong>
        <span className="spacer" />
        <button type="button" onClick={() => setOpen(false)} aria-label={t('bug.close')}>
          <Icon name="close" size={16} />
        </button>
      </div>

      {filed ? (
        <>
          {/* The id is shown because it is the only handle a person has on it
              afterwards -- "the one I filed on Sunday" is not a ticket number. */}
          <p className="ok" role="status">{t('bug.thanks')}</p>
          <p className="muted small"><code>{filed}</code></p>
          <button type="button" onClick={() => setFiled(null)}>{t('bug.another')}</button>
        </>
      ) : (
        <form onSubmit={submit}>
          <label>
            {t('bug.what')}
            <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} required autoFocus />
          </label>
          <label>
            {t('bug.detail')}
            <textarea value={detail} onChange={(e) => setDetail(e.target.value)} rows={4} maxLength={4000} />
          </label>
          <p className="muted small">{t('bug.attached')}</p>
          {error && <p className="error" role="alert">{error}</p>}
          <button type="submit" className="primary" disabled={busy || !title.trim()}>
            {busy ? t('app.loading') : t('bug.send')}
          </button>
        </form>
      )}
    </div>
  )
}
